/*
 * The delivery line under the buy button (snippets/product-delivery-line.liquid),
 * and every other copy of it on the page (home scene, collection tile).
 *
 * Left: when the parcel leaves. On a shipping day before the cut-off time it
 * says "Vandaag verzonden", after it "Morgen verzonden", and across a weekend
 * the name of the next shipping day. Computed from Cortex's ship date and
 * cut-off in Cortex's time zone, so a visitor abroad reads the same promise
 * the warehouse keeps. Until then it holds its place unseen; without a
 * cut-off from Cortex it stays so (no fixed time in the theme: Cortex moves
 * the cut-off per period).
 *
 * Right: what shipping costs for this product: free when its lowest price
 * reaches the threshold (or shipping is always free), otherwise the price and
 * the threshold in one short line, "€6 · gratis vanaf €60" (Thomas 18-10: no
 * surprise at the checkout). The price only from Cortex: the fallback says
 * just "Gratis vanaf ...". An amount only in a
 * euro cart and only for a country Cortex has a threshold for; any other
 * visitor (another currency, or a country Cortex does not know, such as the
 * US) reads "Levering in jouw land" next to the flag, which is true for every
 * country the country selector offers (one market per shipping zone).
 *
 * Under the buy button only, a row below it in the Christmas season: in good
 * time for Christmas with the last order moment for the visitor's country,
 * never a countdown (Thomas 11-10). After the deadline the row says that a
 * parcel arrives after Christmas. The deadline and its time zone come from
 * Cortex (window.JapCortex.christmasMoment); without a deadline for the
 * country the row stays hidden.
 *
 * Sources: Cortex (window.JapCortex.ready, the one source of cut-off,
 * shipping days, ship date, threshold and Christmas deadline); only the
 * threshold has a fallback the snippet put in the data attributes, for while
 * Cortex is silent. Nothing here is a promise of its own: the texts live in
 * the locale files, the numbers come from Cortex or the measured fallback.
 */
(function () {
  const lines = document.querySelectorAll('[data-delivery-line]');
  if (lines.length === 0 || !window.JapShipDate) return;

  // The ship date is the one calculation shared with the cart countdown.
  const { fromCortex } = window.JapShipDate;
  const christmasRow = document.querySelector('[data-delivery-christmas]');

  const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);
  const fallbackMoney = (cents) => `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;

  /** Fills [name] placeholders; a value that is missing leaves its placeholder. */
  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, name) => (values[name] !== undefined ? values[name] : match));
  }

  // ------------------------------------------------------------ the line

  function renderShip(line, ship) {
    const shipEl = line.querySelector('[data-delivery-ship]');
    if (!ship || !shipEl) return;
    const { textToday, textTomorrow, textOn, dayNames } = line.dataset;
    if (ship.offset === 0) shipEl.textContent = textToday;
    else if (ship.offset === 1) shipEl.textContent = textTomorrow;
    else shipEl.textContent = fill(textOn, { day: capitalize(String(dayNames || '').split(',')[ship.weekday]?.trim() || '') });
    line.querySelector('[data-delivery-ship-item]')?.removeAttribute('data-pending');
  }

  /**
   * `withPrice`: the price is Cortex's, so it may be named. The fallback's
   * price is not measured, so then only the threshold is.
   */
  function renderShipping(line, freeFrom, shippingPrice, money, withPrice) {
    const shippingEl = line.querySelector('[data-delivery-shipping]');
    if (!shippingEl) return;
    const price = Number(line.dataset.price) || 0;
    const free = shippingPrice === 0 || (freeFrom !== null && freeFrom > 0 && price >= freeFrom);
    if (free) {
      shippingEl.textContent = line.dataset.textFree;
    } else if (withPrice && shippingPrice > 0 && freeFrom > 0) {
      shippingEl.textContent = fill(line.dataset.textCostFreeFrom, { price: money(shippingPrice), amount: money(freeFrom) });
    } else if (withPrice && shippingPrice > 0) {
      shippingEl.textContent = fill(line.dataset.textCost, { amount: money(shippingPrice) });
    } else if (freeFrom > 0) {
      shippingEl.textContent = fill(line.dataset.textFreeFrom, { amount: money(freeFrom) });
    }
  }

  function renderFromCortex(line, state) {
    renderShip(line, fromCortex(state, { timeZone: state.timeZone, shipDays: line.dataset.shipDays.split(',') }));
    if (state.shipping) {
      renderShipping(line, state.shipping.freeFrom ?? null, state.shipping.price ?? 0, window.JapCortex.shortMoney, true);
    } else {
      // No threshold Cortex can vouch for in this cart (no class for the
      // country, another currency, or a class that does not match the
      // checkout): no amount, only that we deliver here.
      const shippingEl = line.querySelector('[data-delivery-shipping]');
      if (shippingEl) shippingEl.textContent = line.dataset.textShipsToYou;
    }
  }

  // --------------------------------------------------- the Christmas row

  /** A date never breaks over two lines ("22 dec"). */
  const together = (text) => text.replace(/ /g, '\u00a0');

  /** The sentence of the row: the last order moment, never the time that is left. */
  function christmasText(moment, texts) {
    const values = {
      date: together(window.JapCortex.dateLabel(moment.date, { day: 'numeric', month: 'short' })),
      time: moment.time
    };
    switch (moment.phase) {
      case 'early':
        return fill(texts.textEarly, values);
      case 'days':
        return fill(texts.textDays, values);
      case 'last-day':
        return fill(texts.textLastDay, values);
      default:
        return texts.textTooLate;
    }
  }

  /**
   * The Christmas row, again at every new minute while the season runs: the
   * sentence changes at midnight and at the deadline, where it turns to
   * "after Christmas".
   */
  function renderChristmas(state) {
    if (!christmasRow || !window.JapCortex.christmasMoment) return;
    const moment = window.JapCortex.christmasMoment(state, christmasRow.dataset.start);
    const text = moment ? christmasText(moment, christmasRow.dataset) : null;
    christmasRow.querySelector('[data-delivery-christmas-text]').textContent = text || '';
    christmasRow.hidden = !text;
    if (moment) setTimeout(() => renderChristmas(state), 60000 - (Date.now() % 60000) + 50);
  }

  // ------------------------------------------------------------- start

  // 1. The threshold's fallback first, so the right side is right even if
  //    Cortex never answers. It is in euros: another currency keeps the
  //    "Levering in jouw land" the snippet rendered.
  lines.forEach((line) => {
    if (line.dataset.inEuros === 'true') {
      renderShipping(line, Number(line.dataset.freeFrom) || null, Number(line.dataset.shippingPrice) || 0, fallbackMoney, false);
    }
  });

  // 2. Cortex: the promise for the visitor's country.
  if (window.JapCortex && window.JapCortex.ready) {
    window.JapCortex.ready.then((state) => {
      if (!state) return;
      lines.forEach((line) => renderFromCortex(line, state));
      renderChristmas(state);
    });
  }
})();
